import React from "react";
import { Link } from "react-router-dom";
import { Heart, MapPin, Star, Gavel, ImageOff, Truck } from "lucide-react";
import { Badge } from "./ui/badge";
import { resolveImg } from "../lib/api";
import VerifiedBadge from "./VerifiedBadge";

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 3600) return `${Math.max(1, Math.floor(diff / 60))} min fa`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h fa`;
  const d = Math.floor(diff / 86400);
  return d === 1 ? "ieri" : `${d} giorni fa`;
}

// Inline SVG placeholder shown when an image is missing or fails to load.
function ImageFallback({ label }) {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200 text-slate-400">
      <ImageOff className="w-8 h-8 mb-1" strokeWidth={1.5} />
      <div className="text-[10px] font-bold uppercase tracking-wider">Foto non disponibile</div>
      {label && <div className="text-[10px] text-slate-500 mt-1 max-w-[80%] text-center line-clamp-1">{label}</div>}
    </div>
  );
}

function SmartImage({ src, alt, className }) {
  const [errored, setErrored] = React.useState(false);
  const hasSrc = !!src;
  if (!hasSrc || errored) return <ImageFallback label={alt} />;
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={() => setErrored(true)}
      loading="lazy"
    />
  );
}

export default function ListingCard({ listing, auction }) {
  if (auction) {
    const endsIn = new Date(auction.ends_at).getTime() - Date.now();
    const hours = Math.floor(endsIn / 3600000);
    const mins = Math.floor((endsIn % 3600000) / 60000);
    const timeStr = endsIn <= 0 ? "Terminata" : (hours > 24 ? `${Math.floor(hours/24)}g ${hours%24}h` : `${hours}h ${mins}m`);

    return (
      <Link to={`/asta/${auction.id}`} className="listing-card group block rounded-xl overflow-hidden bg-white border border-slate-200">
        <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
          <SmartImage src={resolveImg(auction.image)} alt={auction.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
          <div className="absolute top-2 left-2 flex items-center gap-1 bg-[#e86a24] text-white text-[11px] font-bold px-2 py-1 rounded-full auction-live">
            <Gavel className="w-3 h-3" /> ASTA LIVE
          </div>
          <div className="absolute bottom-2 left-2 right-2 bg-black/70 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-md flex justify-between items-center">
            <span>Termina in</span>
            <span className="font-mono font-bold">{timeStr}</span>
          </div>
        </div>
        <div className="p-3">
          <h3 className="font-semibold text-sm text-slate-900 line-clamp-1">{auction.title}</h3>
          <div className="flex items-baseline gap-1 mt-1">
            <span className="text-xs text-slate-500">Offerta attuale</span>
            <span className="text-lg font-black text-[#e86a24]">€ {Number(auction.current_bid).toLocaleString("it-IT")}</span>
          </div>
          <div className="flex justify-between items-center mt-2 text-xs text-slate-500">
            <span>{auction.bid_count} offerte</span>
            <span className="flex items-center gap-1"><MapPin className="w-3 h-3"/>{auction.castello}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link to={`/annuncio/${listing.id}`} className="listing-card group block rounded-xl overflow-hidden bg-white border border-slate-200">
      <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
        <SmartImage src={resolveImg(listing.images?.[0])} alt={listing.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
        {listing.featured && (
          <Badge className="absolute top-2 left-2 bg-amber-500 hover:bg-amber-500 text-white border-0 text-[10px] font-bold">
            <Star className="w-3 h-3 mr-0.5 fill-white" /> VETRINA
          </Badge>
        )}
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); }}
          className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 backdrop-blur flex items-center justify-center hover:bg-white transition-colors shadow"
          aria-label="Aggiungi ai preferiti"
        >
          <Heart className="w-4 h-4 text-slate-600" />
        </button>
        {listing.allow_barter && (
          <Badge className="absolute bottom-2 left-2 bg-emerald-600 hover:bg-emerald-600 text-white border-0 text-[10px]">
            BARATTO OK
          </Badge>
        )}
        {listing.shipping_from_italy && (
          <Badge className="absolute bottom-2 right-2 bg-green-700 hover:bg-green-700 text-white border-0 text-[10px] flex items-center gap-0.5" data-testid="badge-shipping-italy">
            <Truck className="w-3 h-3" /> 🇮🇹 SPEDITO DA ITALIA
          </Badge>
        )}
      </div>
      <div className="p-3">
        <div className="text-lg font-black text-[#1d3d3a]">€ {Number(listing.price).toLocaleString("it-IT")}</div>
        <h3 className="font-medium text-sm text-slate-700 line-clamp-2 min-h-[2.5rem] mt-1">{listing.title}</h3>
        <div className="flex justify-between items-center mt-2 text-[11px] text-slate-500">
          <span className="flex items-center gap-1">
            <MapPin className="w-3 h-3"/>{listing.castello}
            {listing.user?.is_verified && <VerifiedBadge size="xs" />}
          </span>
          <span>{timeAgo(listing.created_at)}</span>
        </div>
      </div>
    </Link>
  );
}
